// src/pages/testPages/SimplePeriodDropdownTest.tsx
import React, { useState } from 'react';
import { Box, Typography, Paper, Input, Button, Stack, Divider } from '@mui/material';
import { SimplePeriodDropdown } from '../../components';
import { useSelector } from 'react-redux';

// ---- helpers: UNIX seconds (10-digit) ----
const toUnix = (d: Date) => Math.floor(d.getTime() / 1000);
const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
const endOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);

// ---------- Generic ranges ----------
function getTodayRange() {
  const now = new Date();
  return { from: toUnix(startOfDay(now)), to: toUnix(endOfDay(now)) };
}

function getLastNDaysRange(n: number) {
  const now = new Date();
  const end = endOfDay(now);
  const start = startOfDay(new Date(now.getTime() - (n - 1) * 24 * 60 * 60 * 1000));
  return { from: toUnix(start), to: toUnix(end) };
}

// ---------- Fiscal Year helpers (FY = Apr → Mar) ----------
/** Full FY range for a given FY start year (e.g., 2023 for FY 2023–24). */
function getFYRangeForStart(startYear: number) {
  const start = new Date(startYear, 3, 1, 0, 0, 0, 0);         // Apr 1, startYear
  const end = new Date(startYear + 1, 2, 31, 23, 59, 59, 999); // Mar 31, next year
  return { from: toUnix(start), to: toUnix(end) };
}

/** Month in FY order: 1=Apr, 2=May, ... 9=Dec, 10=Jan (next year), 11=Feb, 12=Mar */
function getFYMonthRange(startYear: number, fyMonthId: number) {
  if (fyMonthId < 1 || fyMonthId > 12) throw new Error('fyMonthId must be 1..12');
  let y = startYear;
  let m: number; // 0..11
  if (fyMonthId <= 9) {
    // Apr..Dec in the start year
    m = fyMonthId + 2; // 1->3(Apr), 9->11(Dec)
  } else {
    // Jan..Mar in the next year
    y = startYear + 1;
    m = fyMonthId - 10; // 10->0(Jan), 11->1(Feb), 12->2(Mar)
  }
  const from = new Date(y, m, 1, 0, 0, 0, 0);
  const to = new Date(y, m + 1, 0, 23, 59, 59, 999);
  return { from: toUnix(from), to: toUnix(to) };
}

function getFYQuarterRange(startYear: number, q: 'Q1' | 'Q2' | 'Q3' | 'Q4') {
  let from: Date, to: Date;
  switch (q) {
    case 'Q1': // Apr–Jun (start year)
      from = new Date(startYear, 3, 1, 0, 0, 0, 0);
      to = new Date(startYear, 6, 0, 23, 59, 59, 999); // day 0 of Jul = Jun end
      break;
    case 'Q2': // Jul–Sep
      from = new Date(startYear, 6, 1, 0, 0, 0, 0);
      to = new Date(startYear, 9, 0, 23, 59, 59, 999); // day 0 of Oct = Sep end
      break;
    case 'Q3': // Oct–Dec
      from = new Date(startYear, 9, 1, 0, 0, 0, 0);
      to = new Date(startYear, 12, 0, 23, 59, 59, 999); // day 0 of Jan = Dec end
      break;
    case 'Q4': // Jan–Mar (next year)
      from = new Date(startYear + 1, 0, 1, 0, 0, 0, 0);
      to = new Date(startYear + 1, 3, 0, 23, 59, 59, 999); // day 0 of Apr = Mar end
      break;
  }
  return { from: toUnix(from), to: toUnix(to) };
}

function getFYHalfRange(startYear: number, h: 'H1' | 'H2') {
  let from: Date, to: Date;
  if (h === 'H1') {
    // Apr–Sep (start year)
    from = new Date(startYear, 3, 1, 0, 0, 0, 0);
    to = new Date(startYear, 9, 0, 23, 59, 59, 999); // day 0 of Oct = Sep end
  } else {
    // Oct–Mar (cross-year)
    from = new Date(startYear, 9, 1, 0, 0, 0, 0);
    to = new Date(startYear + 1, 3, 0, 23, 59, 59, 999); // day 0 of Apr = Mar end
  }
  return { from: toUnix(from), to: toUnix(to) };
}

// ---------- UI ----------
const FY_START_YEARS = [2023, 2024, 2025, 2026]; // 2023–24 through 2026–27

const SimplePeriodDropdownTest: React.FC = () => {
  // Optional: current Redux FY (numbers; kept as any per your constraint)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const financialYear: any = useSelector((state: any) => state?.userPrefrenceandReducer?.financialYear);
  const startdate: any = financialYear?.startdate ?? null;
  const enddate: any = financialYear?.enddate ?? null;

  const [range, setRange] = useState<{ from: number | null; to: number | null }>({
    from: null,
    to: null,
  });

  const setAndLog = (r: { from: number; to: number }) => {
    setRange(r);
    // eslint-disable-next-line no-console
    console.log('Programmatically set range (UNIX sec):', r);
  };

  const renderFYBlock = (startYear: number) => (
    <Box key={startYear} sx={{ mt: 2 }}>
      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        FY {startYear}–{String(startYear + 1).slice(-2)}
      </Typography>

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {/* Full FY */}
        <Button variant="outlined" onClick={() => setAndLog(getFYRangeForStart(startYear))}>
          Full FY
        </Button>

        <Divider flexItem orientation="vertical" sx={{ mx: 1 }} />

        {/* Months: Apr→Mar */}
        {[
          { id: 1, label: 'Apr' },
          { id: 2, label: 'May' },
          { id: 3, label: 'Jun' },
          { id: 4, label: 'Jul' },
          { id: 5, label: 'Aug' },
          { id: 6, label: 'Sep' },
          { id: 7, label: 'Oct' },
          { id: 8, label: 'Nov' },
          { id: 9, label: 'Dec' },
          { id: 10, label: 'Jan' },
          { id: 11, label: 'Feb' },
          { id: 12, label: 'Mar' },
        ].map((m) => (
          <Button
            key={`${startYear}-m-${m.id}`}
            variant="outlined"
            onClick={() => setAndLog(getFYMonthRange(startYear, m.id))}
          >
            {m.label}
          </Button>
        ))}

        <Divider flexItem orientation="vertical" sx={{ mx: 1 }} />

        {/* Quarters */}
        {(['Q1', 'Q2', 'Q3', 'Q4'] as const).map((q) => (
          <Button
            key={`${startYear}-q-${q}`}
            variant="outlined"
            onClick={() => setAndLog(getFYQuarterRange(startYear, q))}
          >
            {q}
          </Button>
        ))}

        <Divider flexItem orientation="vertical" sx={{ mx: 1 }} />

        {/* Halves */}
        {(['H1', 'H2'] as const).map((h) => (
          <Button
            key={`${startYear}-h-${h}`}
            variant="outlined"
            onClick={() => setAndLog(getFYHalfRange(startYear, h))}
          >
            {h}
          </Button>
        ))}
      </Stack>
    </Box>
  );

  return (
    <Box sx={{ p: 4 }}>
      <Typography variant="h5" gutterBottom>
        SimplePeriodDropdown — Extended FY Test
      </Typography>

      {/* Just a random input to test focus behavior nearby */}
      <Input placeholder="Focusable input" sx={{ mb: 2 }} />

      <Paper sx={{ p: 2, display: 'inline-block' }}>
        <SimplePeriodDropdown
          value={range}
          onChange={(newRange) => setRange(newRange)}
          title="Select Period:"
          showDateText={true}
          includeToday={true}
        />
      </Paper>

      {/* Quick generic sets */}
      <Box sx={{ mt: 3 }}>
        <Typography variant="body1" gutterBottom>
          <strong>Quick sets:</strong>
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Button variant="outlined" onClick={() => setAndLog(getTodayRange())}>
            Today
          </Button>
          <Button variant="outlined" onClick={() => setAndLog(getLastNDaysRange(7))}>
            Last 7 days
          </Button>
          <Button variant="outlined" onClick={() => setAndLog(getLastNDaysRange(30))}>
            Last 30 days
          </Button>

          {/* If you want to use Redux FY directly */}
          {typeof startdate === 'number' && typeof enddate === 'number' && (
            <Button
              variant="outlined"
              onClick={() =>
                setAndLog({
                  from: startdate > 1e12 ? Math.floor(startdate / 1000) : startdate,
                  to: enddate > 1e12 ? Math.floor(enddate / 1000) : enddate,
                })
              }
            >
              Use Redux FY
            </Button>
          )}
        </Stack>
      </Box>

      {/* FY Blocks: 2023–24 to 2026–27 */}
      <Divider sx={{ my: 3 }} />
      {FY_START_YEARS.map(renderFYBlock)}

      {/* Current selection readout */}
      <Box sx={{ mt: 3 }}>
        <Typography variant="body1" gutterBottom>
          <strong>Selected Range (human + raw):</strong>
        </Typography>
        <Typography variant="body2">
          From: {range.from ? new Date(range.from * 1000).toLocaleDateString() : 'N/A'}
        </Typography>
        <Typography variant="body2">
          To: {range.to ? new Date(range.to * 1000).toLocaleDateString() : 'N/A'}
        </Typography>
        <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
          Raw (UNIX sec): {String(range.from ?? '—')} → {String(range.to ?? '—')}
        </Typography>
      </Box>
    </Box>
  );
};

export { SimplePeriodDropdownTest };
